import * as React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import type { Theme } from '../theme';

export type ButtonVariant =
  | 'primary' | 'secondary' | 'dark' | 'default' | 'dashed' | 'text' | 'link' | 'danger' | 'ghost-danger' | 'soft';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

const sizes = (t: Theme) => ({
  sm: css`height: ${t.control.height.sm}; padding: 0 ${t.control.px.sm}; font-size: ${t.fontSize.xs};`,
  md: css`height: ${t.control.height.md}; padding: 0 ${t.control.px.md}; font-size: ${t.fontSize.base};`,
  lg: css`height: ${t.control.height.lg}; padding: 0 ${t.control.px.lg}; font-size: ${t.fontSize.md};`,
});

const variants = (t: Theme) => ({
  primary: css`
    background: ${t.color.primary.base}; color: ${t.color.primary.on}; border-color: ${t.color.primary.base};
    &:hover { background: ${t.color.primary.hover}; border-color: ${t.color.primary.hover}; }
    &:active { background: ${t.color.primary.active}; border-color: ${t.color.primary.active}; }
  `,
  secondary: css`
    background: ${t.color.secondary.base}; color: ${t.color.secondary.on}; border-color: ${t.color.secondary.base};
    &:hover { background: ${t.color.secondary.hover}; border-color: ${t.color.secondary.hover}; }
    &:active { background: ${t.color.secondary.active}; border-color: ${t.color.secondary.active}; }
  `,
  dark: css`
    background: ${t.color.dark.base}; color: ${t.color.dark.on}; border-color: ${t.color.dark.base};
    &:hover { background: ${t.color.dark.hover}; border-color: ${t.color.dark.hover}; }
    &:active { background: ${t.color.dark.active}; border-color: ${t.color.dark.active}; }
  `,
  default: css`
    background: ${t.color.bg.default}; color: ${t.color.fg.default}; border-color: ${t.color.border.strong};
    &:hover { color: ${t.color.primary.base}; border-color: ${t.color.primary.base}; }
    &:active { color: ${t.color.primary.active}; border-color: ${t.color.primary.active}; }
  `,
  dashed: css`
    background: ${t.color.bg.default}; color: ${t.color.fg.default}; border: 1px dashed ${t.color.border.strong};
    &:hover { color: ${t.color.primary.base}; border-color: ${t.color.primary.base}; }
  `,
  text: css`
    background: transparent; color: ${t.color.fg.default};
    &:hover { background: ${t.color.bg.muted}; }
  `,
  link: css`
    background: transparent; color: ${t.color.primary.base}; padding: 0; height: auto; border: 0;
    &:hover { color: ${t.color.primary.hover}; text-decoration: underline; text-underline-offset: 3px; }
  `,
  danger: css`
    background: ${t.color.danger}; color: #fff; border-color: ${t.color.danger};
    &:hover { background: ${t.color.red[700]}; border-color: ${t.color.red[700]}; }
  `,
  'ghost-danger': css`
    background: ${t.color.bg.default}; color: ${t.color.danger}; border-color: ${t.color.border.strong};
    &:hover { color: ${t.color.red[700]}; border-color: ${t.color.danger}; }
  `,
  soft: css`
    background: ${t.color.primary.soft}; color: ${t.color.primary.base}; border-color: transparent;
    &:hover { background: ${t.color.primary.soft2}; color: ${t.color.blue[500]}; }
  `,
});

const StyledButton = styled.button<{ $variant: ButtonVariant; $size: ButtonSize }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border-radius: ${({ theme }) => theme.radius.base};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  font-family: inherit;
  cursor: pointer;
  border: 1px solid transparent;
  white-space: nowrap;
  user-select: none;
  text-decoration: none;
  &:hover { text-decoration: none; }
  transition: background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.ease},
              border-color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.ease},
              color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.ease},
              box-shadow ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.ease};

  ${({ theme, $size }) => sizes(theme)[$size]}
  ${({ theme, $variant }) => variants(theme)[$variant]}

  &:focus-visible { outline: none; box-shadow: 0 0 0 3px ${({ theme }) => theme.color.blue[100]}; }

  &:disabled {
    cursor: not-allowed;
    background: ${({ theme }) => theme.color.bg.muted};
    color: ${({ theme }) => theme.color.fg.subtle};
    border-color: ${({ theme }) => theme.color.border.default};
  }
`;

/** Lumen button — one primary action per view. See variants above. */
export function Button({ variant = 'default', size = 'md', ...rest }: ButtonProps) {
  return <StyledButton $variant={variant} $size={size} {...rest} />;
}
